import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { Logo } from "@/components/site/Header";

export default function CompletePayment() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/payments/resume/${token}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/resume/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="complete-payment-page" className="min-h-screen max-w-xl mx-auto px-5 py-12">
      <Link to="/" className="flex items-center gap-3 mb-12"><Logo size={36} /><span className="font-display font-bold text-sm">Elite Performance Lab</span></Link>
      {error && (
        <div>
          <p data-testid="complete-payment-error" className="text-red-300">{error}</p>
          <Link to="/register" className="btn-primary mt-8" data-testid="complete-payment-register-link">Return to registration</Link>
        </div>
      )}
      {!d && !error && <Loader2 className="animate-spin accent-text" />}
      {d && (
        <div>
          <p className="eyebrow">{d.paid ? "Paid" : d.total_cents === 0 ? "Nothing to pay" : "Payment pending"}</p>
          <h1 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">{d.paid ? "This registration is already paid" : "Complete your registration"}</h1>
          <p className="mt-3 text-slate-400">{d.camp_name} · {d.dates_label} · {d.venue}</p>
          <div className="mt-8 space-y-3">
            {d.players.map((p, i) => (
              <div key={i} data-testid={`resume-player-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex justify-between">
                <div><p className="font-display font-bold text-lg">{p.first_name} {p.last_name}</p><p className="text-sm accent-text">{p.group_name}</p></div>
                <p className="font-mono-epl">{money(p.amount_cents)}</p>
              </div>
            ))}
          </div>
          {!d.paid && d.total_cents === 0 && (
            <div data-testid="resume-nothing-due">
              <p className="mt-8 text-slate-400">There is nothing left to pay on this link — these players have been registered again separately.</p>
              <Link to="/register" className="btn-primary mt-6">Return to registration</Link>
            </div>
          )}
          {!d.paid && d.total_cents > 0 && (
            <>
              <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-6">
                <p className="text-xs text-slate-500">Includes GST of {money(Math.round(d.total_cents / 11))}</p>
                <p data-testid="resume-total" className="font-display font-extrabold text-4xl tracking-tight">{money(d.total_cents)}</p>
              </div>
              {d.pay_per_player && d.players.filter((p) => p.status !== "confirmed").length > 1 && (
                <p data-testid="resume-per-player-note" className="mt-3 text-sm text-slate-400">You'll pay {money(d.players.find((p) => p.status !== "confirmed").amount_cents)} for each player separately, one after another.</p>
              )}
              <p className="mt-6 text-sm text-slate-400">
                By paying you confirm the <Link to="/terms" target="_blank" className="accent-text underline">Terms</Link> and <Link to="/refund-policy" target="_blank" className="accent-text underline">Refund & Cancellation Policy</Link> you accepted at registration.
              </p>
              {d.is_open ? (
                <button data-testid="resume-pay" onClick={pay} disabled={busy} className="btn-primary w-full mt-8 disabled:opacity-60">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <><Lock size={16} />Continue to secure payment</>}
                </button>
              ) : (
                <p data-testid="resume-closed" className="mt-8 text-red-300">Registrations are closed, so this registration can no longer be paid.</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
